import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { palette } from "../../../../assets/styles/Palette";
import {
  Body1,
  Body2,
  Caption1,
  Body3,
} from "../../../../assets/styles/Typography";
import {
  IS_LOGGED_IN,
  AUTODISPLAY_CONTENT_LIST,
  AUTODISPLAY_PREVIEW_HTML,
  AUTODISPLAY_INTERVER_TIME,
  AUTODISPLAY_DISPLAY_WIDTH,
  AUTODISPLAY_DISPLAY_HEIGHT,
} from "../../../AtomStates";
import { useAtom } from "jotai";
import {
  getDisplayListOnServer,
  getViewListOnServer,
  updateViewOnServer,
  getViewDetailOnServer,
  updateViewOnServerPostManagement,
} from "../../../../utils/indexedDB";
import PopupWrap from "../../../../assets/styles/Popup";
import { BoxWrap } from "../../../../assets/styles/BusinessAnalysisStyle";
import images from "../../../../assets/styles/Images";
import { Button } from "../../../../assets/styles/ButtonStyle";
import OrganismTemplatePreview from "../../../Global/components/organisms/OrganismTemplatePreview";

const OrganismDashboardContentList = ({ handleCreateContent }) => {
  const [isLoggedIn, setIsLoggedIn] = useAtom(IS_LOGGED_IN);
  const [contentList, setContentList] = useAtom(AUTODISPLAY_CONTENT_LIST);
  const [previewHtmlContent, setPreviewHtmlContent] = useAtom(
    AUTODISPLAY_PREVIEW_HTML
  );
  const [autodisplayIntervalTime, setAutodisplayIntervalTime] = useAtom(
    AUTODISPLAY_INTERVER_TIME
  );

  const [displayWidth, setDisplayWidth] = useAtom(AUTODISPLAY_DISPLAY_WIDTH);
  const [displayHeight, setDisplayHeight] = useAtom(AUTODISPLAY_DISPLAY_HEIGHT);

  const [showPopup, setShowPopup] = useState(false);
  const [selectedContentId, setSelectedContentId] = useState(null);
  const [originalActivateCode, setOriginalActivateCode] = useState([]); // 원본 데이터 저장
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [templateToPreview, setTemplateToPreview] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewWidth, setPreviewWidth] = useState(0);
  const [previewHeight, setPreviewHeight] = useState(0);
  const [pendingToggle, setPendingToggle] = useState(null); // { index, newState }

  const [showActivatePopup, setShowActivatePopup] = useState(false);
  const [showDeactivatePopup, setShowDeactivatePopup] = useState(false);
  const [showCopySuccessPopup, setShowCopySuccessPopup] = useState(false);
  const [copiedInfo, setCopiedInfo] = useState(null);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [contentToDelete, setContentToDelete] = useState(null);

  // 토글 스위치 컴포넌트
  const ToggleSwitch = ({ isActive, onChange }) => {
    return (
      <ToggleContainer>
        <ToggleSwitchArea
          onClick={() => {
            onChange(!isActive);
          }}
        >
          <ToggleSlider isActive={isActive}>
            <ToggleKnob isActive={isActive} />
          </ToggleSlider>
        </ToggleSwitchArea>
        <ToggleLabelArea>
          <ToggleLabel isActive={isActive}>
            {isActive ? "활성화" : "비활성화"}
          </ToggleLabel>
        </ToggleLabelArea>
      </ToggleContainer>
    );
  };

  const handleCopyToClipboard = async (content, type) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedInfo({ type, content });
      setShowCopySuccessPopup(true);
    } catch (error) {
      console.error("복사 실패:", error);
      // 복사 실패 시에도 사용자에게 알림
      setCopiedInfo({ type: "오류", content: "복사에 실패했습니다." });
      setShowCopySuccessPopup(true);
    }
  };

  // 팝업 열기 - 서버에서 최신 데이터 가져오기
  const handleOpenPopup = async (contentId) => {
    try {
      setSelectedContentId(contentId);
      setShowPopup(true);

      // 서버에서 최신 데이터 가져오기
      const latestContent = await getViewDetailOnServer(contentId, isLoggedIn);

      setPreviewHtmlContent(latestContent.htmlCode);

      if (latestContent) {
        // activateCheckTimestamp 확인 및 activeState 업데이트
        const currentTime = Date.now();
        const baseTime = autodisplayIntervalTime;

        let hasUpdates = false; // 변경사항이 있는지 확인

        const updatedActivateCode =
          latestContent.activateCode?.map((code) => {
            let newCode = { ...code };
            // activateCheckTimestamp가 있고, 현재 시간보다 10분 이상 뒤쳐진 경우
            if (newCode.activateCheckTimestamp && newCode.activeState === 1) {
              const checkTime = new Date(
                newCode.activateCheckTimestamp
              ).getTime();
              const timeDifference = currentTime - checkTime;

              if (timeDifference > baseTime) {
                hasUpdates = true;
                newCode.activeState = 0;
              }
            }

            // activeState가 0이면 관련 필드들을 초기화합니다.
            if (newCode.activeState === 0) {
              if (
                newCode.activeIp ||
                newCode.activateLocation ||
                newCode.activeTimestamp
              ) {
                newCode.activeIp = "";
                newCode.activateLocation = "";
                newCode.activeTimestamp = "";
                hasUpdates = true; // 데이터가 변경되었으므로 업데이트 플래그를 설정합니다.
              }
            }

            return newCode;
          }) || [];

        // 업데이트된 activateCode를 포함한 latestContent 생성
        const updatedLatestContent = {
          ...latestContent,
          activateCode: updatedActivateCode,
        };

        // 변경사항이 있으면 서버에도 업데이트
        if (hasUpdates) {
          try {
            await updateViewOnServerPostManagement(contentId, {
              activateCode: updatedActivateCode,
            });
            console.log("서버에 activeState 업데이트 완료");
          } catch (serverError) {
            console.error("서버 업데이트 중 오류 발생:", serverError);
          }
        }

        // 로컬 상태 업데이트
        setContentList((prev) =>
          prev.map((content) =>
            content._id === contentId
              ? { ...content, ...updatedLatestContent }
              : content
          )
        );

        // 원본 데이터 저장 (업데이트된 데이터 기준)
        setOriginalActivateCode(
          updatedActivateCode.length > 0 ? [...updatedActivateCode] : []
        );
      } else {
        // 기존 로컬 데이터 사용
        const content = contentList.find((c) => c._id === contentId);
        setOriginalActivateCode(
          content?.activateCode ? [...content.activateCode] : []
        );
      }
    } catch (error) {
      console.error("팝업 열기 중 오류 발생:", error);

      // 에러 시 기존 로컬 데이터 사용
      const content = contentList.find((c) => c._id === contentId);
      setOriginalActivateCode(
        content?.activateCode ? [...content.activateCode] : []
      );
    }
  };

  // 팝업 닫기
  const handleClosePopup = () => {
    setShowPopup(false);
    setSelectedContentId(null);
    setOriginalActivateCode([]);
  };

  // 저장 처리
  const handleSave = async () => {
    try {
      if (!selectedContent || pendingToggle === null) return;

      const { index, newState } = pendingToggle;

      // 로컬 상태를 업데이트하기 위한 새 activateCode 배열 생성
      const updatedActivateCode = selectedContent.activateCode.map(
        (code, idx) =>
          idx === index
            ? {
                ...code,
                state: newState ? 1 : 0,
                // 비활성화로 바꿀 때만 activeState를 0으로 설정
                ...(newState ? {} : { activeState: 0 }),
              }
            : code
      );

      // 비활성화되는 항목들의 모든 관련 필드를 초기화
      const finalActivateCode = updatedActivateCode.map((code) => ({
        ...code,
        // 비활성화로 바꿀 때 모든 관련 필드 초기화
        ...(code.state === 0
          ? {
              activeState: 0,
              activeTimestamp: "",
              activateLocation: "",
              activeIp: "",
            }
          : {}),
      }));

      // 서버에 업데이트
      await updateViewOnServerPostManagement(selectedContentId, {
        activateCode: finalActivateCode,
      });

      // 로컬 상태도 업데이트된 데이터로 동기화
      setContentList((prev) =>
        prev.map((content) =>
          content._id === selectedContentId
            ? { ...content, activateCode: finalActivateCode }
            : content
        )
      );

      // 저장 후 원본 데이터 업데이트
      setOriginalActivateCode([...finalActivateCode]);

      if (showActivatePopup) {
        setShowActivatePopup(false);
      } else if (showDeactivatePopup) {
        setShowDeactivatePopup(false);
      }
      setPendingToggle(null);
    } catch (error) {
      console.error("서버 저장 중 오류 발생:", error);
    }
  };

  // 선택된 콘텐츠 찾기
  const selectedContent = contentList.find(
    (content) => content._id === selectedContentId
  );

  const handleTemplatePreview = async (contentId) => {
    try {
      // 서버에서 최신 데이터 가져오기
      const latestContent = await getViewDetailOnServer(contentId, isLoggedIn);

      // HTML 콘텐츠 설정
      setPreviewHtml(latestContent.htmlCode);

      setPreviewWidth(parseInt(latestContent.viewDevice.deviceWidth));
      setPreviewHeight(parseInt(latestContent.viewDevice.deviceHeight));

      setTemplateToPreview(latestContent.designConcept);
      setShowPopup(false);
      setShowTemplatePreview(true);
    } catch (error) {
      console.error("미리보기 데이터 로딩 실패:", error);

      // 에러 시 기존 로컬 데이터 사용
      const content = contentList.find((c) => c._id === contentId);
      if (content?.htmlCode) {
        setPreviewHtml(content.htmlCode);
      }
      if (content?.viewDevice) {
        setPreviewWidth(parseInt(content.viewDevice.deviceWidth));
        setPreviewHeight(parseInt(content.viewDevice.deviceHeight));
      }

      setTemplateToPreview(contentId);
      setShowPopup(false);
      setShowTemplatePreview(true);
    }
  };

  const handleCloseTemplatePreview = () => {
    setShowTemplatePreview(false);
    setTemplateToPreview(null);
  };

  const handleDeleteContent = async (contentId) => {
    console.log("contentId", contentId);
    await updateViewOnServer(contentId, { deleteState: 1 });
    setContentList((prev) => prev.filter((c) => c._id !== contentId));
  };

  return (
    <>
      <ContentListWrap>
        {contentList.length === 0 ? (
          <BoxWrap NoData style={{ height: "300px" }}>
            <img src={images.ListFillPrimary} alt="" />
            <Body2 color="gray700" align="center !important">
              아직 등록된 콘텐츠가 없어요. <br />
              콘텐츠를 등록하고 화면을 구성해보세요.
            </Body2>

            <Button
              Medium
              Outline
              // Fill
              // primaryLightest
              onClick={handleCreateContent}
            >
              <Body2 color="gray700">콘텐츠 등록하기</Body2>
            </Button>
          </BoxWrap>
        ) : (
          <Table>
            <colgroup>
              <col width="36%" />
              <col width="22%" />
              <col width="14%" />
              {/* <col width="28%" /> */}
              <col width="28%" />
            </colgroup>

            <TableHeader>
              <tr>
                <th>
                  <Body1 color="gray700" align="left">
                    콘텐츠 명
                  </Body1>
                </th>

                <th>
                  <Body1 color="gray700" align="left">
                    디스플레이 정보
                  </Body1>
                </th>

                <th>
                  <Body1 color="gray700" align="left">
                    제작일
                  </Body1>
                </th>
                {/* <th>
                  <Body1 color="gray700" align="left">
                    콘텐츠 설정
                  </Body1>
                </th> */}

                <th>
                  <Body1 color="gray700" align="left">
                    콘텐츠 설정
                  </Body1>
                </th>
              </tr>
            </TableHeader>

            <TableBody>
              {contentList.map((content) => (
                <tr key={content._id}>
                  <td>
                    <ContentNameCell>
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          flexWrap: "wrap",
                          marginBottom: "8px",
                        }}
                      >
                        <TemplateCategory>
                          <Body3 color="primary" style={{ fontSize: "12px" }}>
                            {content?.designConcept}
                          </Body3>
                        </TemplateCategory>

                        {content?.contentsPurpose && (
                          <TemplateCategory>
                            <Body3 color="primary" style={{ fontSize: "12px" }}>
                              {content.contentsPurpose === "information"
                                ? "정보전달형"
                                : content.contentsPurpose === "process"
                                ? "프로세스 안내형"
                                : content.contentsPurpose === "warning"
                                ? "경고/주의형"
                                : content.contentsPurpose}
                            </Body3>
                          </TemplateCategory>
                        )}
                      </div>

                      <Body2 color="gray700">
                        {content.dataAnalysis?.title}
                      </Body2>
                    </ContentNameCell>
                  </td>
                  <td>
                    <Body2
                      color="gray700"
                      align="left"
                      style={{ whiteSpace: "nowrap", textAlign: "left" }}
                    >
                      {/* {content.viewDevice?.deviceDPI || "FHD (1920×1080)"} */}
                      {content.viewDevice?.deviceDPI} <br />
                      <Body2
                        color="gray700"
                        align="center"
                        style={{ whiteSpace: "nowrap", textAlign: "left" }}
                      >
                        {content.viewDevice?.deviceInch} (
                        {content.viewDevice?.deviceCm})
                      </Body2>
                    </Body2>
                  </td>
                  <td>
                    <Body2 color="gray700" align="left">
                      {(() => {
                        const dateStr = content.createDate;
                        // "2023. 12. 15. 오전 10:30:45" 형태에서 분리
                        const match = dateStr.match(
                          /^(.+?)\s+(오전|오후)\s+(.+)$/
                        );
                        if (match) {
                          const datePart = match[1]; // "2023. 12. 15."
                          const amPm = match[2]; // "오전" 또는 "오후"
                          const timePart = match[3]; // "10:30:45"
                          return (
                            <>
                              {datePart}
                              <br />
                              <Body2 color="gray700" align="left">
                                {amPm} {timePart}
                              </Body2>
                            </>
                          );
                        }
                        return dateStr;
                      })()}
                    </Body2>
                  </td>
                  <td>
                    <ActionButtonsCell>
                      <PreviewButton
                        onClick={() => handleTemplatePreview(content._id)}
                      >
                        <svg
                          width="21"
                          height="21"
                          viewBox="0 0 21 21"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10.4991 4.9375C14.2651 4.9376 17.6532 7.00578 18.712 9.81836C18.8158 10.0941 18.8308 10.4038 18.7374 10.6992C18.0917 12.7392 15.547 15.4374 10.4991 15.4375C5.43073 15.4375 2.93409 12.476 2.27936 10.7217C2.17268 10.4358 2.17158 10.1309 2.25983 9.85352C2.7197 8.40882 3.86624 7.17982 5.3194 6.32129C6.77927 5.4589 8.5994 4.9375 10.4991 4.9375ZM10.4991 6.27051C8.83591 6.27051 7.25005 6.7295 5.99713 7.46973C4.73751 8.21395 3.86349 9.2112 3.53034 10.2578L3.52936 10.2598C4.00367 11.5242 6.02377 14.1045 10.4991 14.1045C15.0269 14.1044 17.0129 11.7269 17.4659 10.2988V10.2979C17.4658 10.2964 17.4657 10.2929 17.4639 10.2881C16.6478 8.12022 13.8568 6.27061 10.4991 6.27051ZM10.4991 7.02148C12.2479 7.02148 13.666 8.43867 13.6661 10.1875C13.6661 11.9364 12.248 13.3545 10.4991 13.3545C8.75034 13.3543 7.33307 11.9363 7.33307 10.1875C7.33316 8.43878 8.75039 7.02166 10.4991 7.02148ZM10.4991 8.35449C9.48677 8.35467 8.66617 9.17516 8.66608 10.1875C8.66608 11.1999 9.48671 12.0213 10.4991 12.0215C11.5116 12.0215 12.3331 11.2 12.3331 10.1875C12.333 9.17505 11.5116 8.35449 10.4991 8.35449Z"
                            fill="#666666"
                          />
                        </svg>
                        미리보기
                      </PreviewButton>
                      <PopupButton onClick={() => handleOpenPopup(content._id)}>
                        <svg
                          width="21"
                          height="21"
                          viewBox="0 0 21 21"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M11.2461 1.51953C11.549 1.51953 11.8136 1.72371 11.8906 2.0166L12.3047 3.5918L13.7861 4.20117L14.8652 3.2793C15.1297 3.05341 15.5235 3.06854 15.7695 3.31445L17.3691 4.91504C17.6142 5.1601 17.6309 5.55172 17.4072 5.81641L16.4893 6.90234L17.084 8.33887L18.6621 8.74023C18.9575 8.81535 19.1649 9.08099 19.165 9.38574V10.9678C19.165 11.2713 18.9597 11.5368 18.666 11.6133L17.0781 12.0264L16.4824 13.4648L17.4062 14.5547C17.6306 14.8193 17.6145 15.2127 17.3691 15.458L15.7695 17.0576C15.5191 17.308 15.1162 17.3185 14.8525 17.082L14.8086 17.043C14.7798 17.0173 14.7385 16.9798 14.6875 16.9346C14.5853 16.8439 14.4453 16.7207 14.2949 16.5898C14.121 16.4384 13.9438 16.2868 13.7959 16.1641L12.3457 16.7646L11.9443 18.3496C11.8694 18.6454 11.603 18.8525 11.2979 18.8525H9.69824C9.39301 18.8525 9.12661 18.6455 9.05176 18.3496L8.65137 16.7646L7.24414 16.1855L6.11914 17.1025C5.85403 17.3186 5.4684 17.2994 5.22656 17.0576L3.62695 15.458C3.37715 15.2082 3.36562 14.8067 3.60059 14.543L4.53223 13.4971L3.93066 12.0684L2.32227 11.6289C2.03252 11.5497 1.83115 11.2867 1.83105 10.9863V9.38574C1.83123 9.07608 2.04509 8.80769 2.34668 8.7373L3.9082 8.37207L4.49414 6.93164L3.58105 5.80664C3.36564 5.5415 3.3854 5.1566 3.62695 4.91504L5.22656 3.31445C5.47615 3.06496 5.87684 3.05367 6.14062 3.28809L7.19043 4.2207L8.61621 3.62012L9.03809 2.0166C9.11514 1.7238 9.37987 1.51963 9.68262 1.51953H11.2461ZM9.82227 4.27637C9.76963 4.47637 9.62709 4.64132 9.43652 4.72168L7.32031 5.61328C7.08353 5.713 6.81015 5.66783 6.61816 5.49707L5.72461 4.70215L4.99414 5.43262L5.77832 6.39648C5.93173 6.5853 5.96959 6.84301 5.87793 7.06836L5.01367 9.19434C4.93216 9.39473 4.75851 9.54355 4.54785 9.59277L3.16504 9.91504V10.4775L4.59473 10.8672C4.79213 10.9211 4.95371 11.0634 5.0332 11.252L5.9248 13.3672C6.02461 13.6041 5.97958 13.8773 5.80859 14.0693L5.01465 14.96L5.74414 15.6885L6.70996 14.9014C6.89955 14.747 7.15864 14.7087 7.38477 14.8018L9.46973 15.6592C9.6648 15.7395 9.81054 15.9078 9.8623 16.1123L10.2168 17.5195H10.7793L11.1357 16.1133C11.1874 15.9093 11.332 15.7417 11.5264 15.6611L13.6475 14.7822C13.8599 14.6944 14.103 14.7227 14.29 14.8564C14.4982 15.0053 14.8731 15.3256 15.1699 15.584C15.2038 15.6135 15.238 15.6425 15.2705 15.6709L15.9922 14.9492L15.2021 14.0166C15.0407 13.8261 14.9992 13.5608 15.0947 13.3301L15.9736 11.209C16.0536 11.0163 16.219 10.872 16.4209 10.8193L17.8311 10.4521V9.9043L16.4316 9.54883C16.2279 9.49701 16.0609 9.3515 15.9805 9.15723L15.1016 7.03711C15.0061 6.80662 15.0479 6.54206 15.209 6.35156L15.9932 5.42383L15.2627 4.69336L14.3428 5.47949C14.1526 5.64188 13.8875 5.68398 13.6562 5.58887L11.4912 4.69824C11.2979 4.61875 11.1527 4.45312 11.0996 4.25098L10.7324 2.85254H10.1963L9.82227 4.27637ZM10.499 7.01953C12.248 7.01953 13.666 8.43758 13.666 10.1865C13.6658 11.9353 12.2479 13.3525 10.499 13.3525C8.75034 13.3524 7.33318 11.9352 7.33301 10.1865C7.33301 8.43769 8.75023 7.01971 10.499 7.01953ZM10.499 8.35254C9.48661 8.35271 8.66602 9.17407 8.66602 10.1865C8.66619 11.1988 9.48672 12.0194 10.499 12.0195C11.5115 12.0195 12.3328 11.1989 12.333 10.1865C12.333 9.17396 11.5116 8.35254 10.499 8.35254Z"
                            fill="#226FFF"
                          />
                        </svg>
                        활성화 설정
                      </PopupButton>
                      <DeleteButton
                        onClick={() => {
                          setContentToDelete(content._id);
                          setShowConfirmPopup(true);
                        }}
                      >
                        <svg
                          width="20"
                          height="21"
                          viewBox="0 0 20 21"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g clipPath="url(#clip0_992_2924)">
                            <path
                              d="M16.8364 3.70215C17.2366 3.70215 17.5618 4.02663 17.562 4.42676C17.562 4.82711 17.2368 5.15234 16.8364 5.15234H16.4419V15.3652C16.4419 17.367 14.8196 18.9902 12.8179 18.9902H6.71533C4.71361 18.9902 3.09131 17.367 3.09131 15.3652V5.15234H2.69678C2.29664 5.1521 1.97217 4.82695 1.97217 4.42676C1.97243 4.02678 2.2968 3.70239 2.69678 3.70215H16.8364ZM4.54053 5.15234V15.3652C4.54053 16.5663 5.5143 17.54 6.71533 17.54H12.8179C14.0189 17.54 14.9927 16.5663 14.9927 15.3652V5.15234H4.54053ZM8.24854 7.12402C8.64858 7.12419 8.97289 7.44861 8.97314 7.84863V14.5791C8.97314 14.9793 8.64874 15.3045 8.24854 15.3047C7.84819 15.3047 7.52295 14.9794 7.52295 14.5791V7.84863C7.52321 7.44851 7.84835 7.12402 8.24854 7.12402ZM11.2837 7.12402C11.6839 7.12402 12.009 7.44851 12.0093 7.84863V14.5791C12.0093 14.9794 11.684 15.3047 11.2837 15.3047C10.8835 15.3045 10.5591 14.9794 10.5591 14.5791V7.84863C10.5593 7.4486 10.8836 7.12417 11.2837 7.12402ZM12.064 0.988281C12.4641 0.988474 12.7884 1.31279 12.7886 1.71289C12.7886 2.11312 12.4641 2.43828 12.064 2.43848H7.47119C7.07096 2.43834 6.74658 2.11315 6.74658 1.71289C6.74674 1.31276 7.07106 0.988422 7.47119 0.988281H12.064Z"
                              fill="#666666"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_992_2924">
                              <rect
                                width="20"
                                height="21"
                                fill="white"
                                transform="translate(0 0.1875)"
                              />
                            </clipPath>
                          </defs>
                        </svg>
                      </DeleteButton>
                    </ActionButtonsCell>
                  </td>
                  <td>
                    <Body2 color="gray700" align="left">
                      <span
                        style={{
                          color: "#256EF4" /* palette.primaryLightest */,
                        }}
                      >
                        {content.activateState?.active}
                      </span>
                      <span style={{ color: "#464C53" /* palette.gray700 */ }}>
                        {` / ${content.activateState?.total}`}
                      </span>
                    </Body2>
                  </td>
                </tr>
              ))}
            </TableBody>
          </Table>
        )}
      </ContentListWrap>

      {/* 커스텀 팝업 */}
      {showPopup && selectedContent && (
        <CustomPopupOverlay>
          <CustomPopupContainer>
            {/* 헤더 */}
            <CustomPopupHeader>
              <CustomPopupTitleSection>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    width: "100%",
                  }}
                >
                  <PopupTitleContainer>
                    <TagContainer>
                      <TemplateCategory>
                        <Body3 color="primary" style={{ fontSize: "12px" }}>
                          {selectedContent.designConcept}
                        </Body3>
                      </TemplateCategory>

                      {selectedContent?.contentsPurpose && (
                        <ContentPurpose>
                          <Body3 style={{ fontSize: "12px" }}>
                            {selectedContent.contentsPurpose === "information"
                              ? "정보전달형"
                              : selectedContent.contentsPurpose === "process"
                              ? "프로세스 안내형"
                              : selectedContent.contentsPurpose === "warning"
                              ? "경고/주의형"
                              : selectedContent.contentsPurpose}
                          </Body3>
                        </ContentPurpose>
                      )}
                    </TagContainer>
                    <CustomPopupTitle>
                      {selectedContent.dataAnalysis?.title}
                    </CustomPopupTitle>
                  </PopupTitleContainer>

                  <CustomCloseButton onClick={handleClosePopup}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M18 6L6 18M6 6L18 18"
                        stroke="#6B7280"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </CustomCloseButton>
                </div>
                <StyledInfoContainer>
                  <StyledLabel>
                    <StyledMeta>
                      <StyledInfoSpan>
                        해상도 정보:{" "}
                        {selectedContent.viewDevice?.deviceDPIInfo ||
                          "FHD (1920×1080)"}
                      </StyledInfoSpan>
                      <StyledDivider />
                      <StyledInfoSpan>
                        인치 정보:{" "}
                        {selectedContent.viewDevice?.deviceInch ||
                          "27인치 (68cm)"}
                      </StyledInfoSpan>
                      {/* <StyledDivider />
                      <StyledInfoSpan>
                        사이즈 정보 (mm):{" "}

                        {selectedContent.viewDevice?.deviceWidth} x {selectedContent.viewDevice?.deviceHeight}
                      </StyledInfoSpan> */}
                    </StyledMeta>
                  </StyledLabel>
                  <StyledFrame>
                    <div>
                      <StyledIdSpan01>경로 ID : </StyledIdSpan01>
                      <StyledIdSpan02>
                        {selectedContent.viewId || selectedContent._id}
                      </StyledIdSpan02>
                    </div>
                    <CopyButton
                      onClick={() =>
                        navigator.clipboard.writeText(
                          selectedContent.viewId || selectedContent._id
                        )
                      }
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z"
                          fill="#9CA3AF"
                        />
                      </svg>
                    </CopyButton>
                  </StyledFrame>
                </StyledInfoContainer>
              </CustomPopupTitleSection>
            </CustomPopupHeader>

            {/* 바디 */}
            <CustomPopupBody>
              <StyledTableContainer>
                <PopupTable>
                  <colgroup>
                    <col width="18%" />
                    <col width="27%" />
                    <col width="23%" />
                    <col width="14%" />
                    <col width="18%" />
                  </colgroup>

                  <PopupTableHeader>
                    <tr>
                      <th>
                        <Body1
                          color="gray800"
                          align="center"
                          style={{ whiteSpace: "nowrap" }}
                        >
                          활성화 여부
                        </Body1>
                      </th>
                      <th>
                        <Body1 color="gray800" align="left">
                          활성화 CODE
                        </Body1>
                      </th>
                      <th>
                        <Body1 color="gray800" align="left">
                          디스플레이 정보
                        </Body1>
                      </th>
                      <th>
                        <Body1
                          color="gray800"
                          align="left"
                          style={{ whiteSpace: "nowrap" }}
                        >
                          활성화 시간
                        </Body1>
                      </th>
                      <th>
                        <Body1 color="gray800" align="left">
                          게시 상태
                        </Body1>
                      </th>
                    </tr>
                  </PopupTableHeader>

                  <PopupTableBody>
                    {selectedContent.activateCode &&
                    selectedContent.activateCode.length > 0 ? (
                      selectedContent.activateCode.map((codeItem, index) => (
                        <tr key={index}>
                          <td>
                            <PopupToggleCell>
                              <ToggleSwitch
                                isActive={codeItem.state === 1}
                                onChange={(newState) => {
                                  setPendingToggle({ index, newState });
                                  if (newState) {
                                    setShowActivatePopup(true);
                                  } else {
                                    setShowDeactivatePopup(true);
                                  }
                                }}
                              />
                            </PopupToggleCell>
                          </td>
                          <td>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <Body2>{codeItem.code}</Body2>
                              {codeItem.state === 1 && (
                                <CopyButton
                                  onClick={() =>
                                    handleCopyToClipboard(
                                      codeItem.code,
                                      "활성화 CODE"
                                    )
                                  }
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                  >
                                    <path
                                      d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z"
                                      fill="#9CA3AF"
                                    />
                                  </svg>
                                </CopyButton>
                              )}
                            </div>
                          </td>
                          <td>
                            {!codeItem.activeIp &&
                            !codeItem.activateLocation ? (
                              <Body2
                                align="left"
                                style={{
                                  fontSize: "14px",
                                  fontWeight: "400",
                                  color: palette.gray500,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                활성화 후 자동 등록됩니다
                              </Body2>
                            ) : (
                              <>
                                <Body2
                                  color="gray700"
                                  align="left"
                                  style={{ fontSize: "14px" }}
                                >
                                  {codeItem.activeIp || "-"}
                                </Body2>
                                <Body2
                                  color="gray500"
                                  align="left"
                                  style={{ fontSize: "16px", marginTop: "2px" }}
                                >
                                  {codeItem.activateLocation || "-"}
                                </Body2>
                              </>
                            )}
                          </td>
                          <td>
                            <Body2
                              color="gray700"
                              align="left"
                              style={{ fontSize: "14px", whiteSpace: "nowrap" }}
                            >
                              {codeItem.activeTimestamp
                                ? new Date(
                                    codeItem.activeTimestamp
                                  ).toLocaleString("ko-KR", {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : "-"}
                            </Body2>
                          </td>
                          <td>
                            {codeItem.activeState === 1 ? (
                              <PublishedStatusButton>
                                <PublishedStatusText>
                                  게시중
                                </PublishedStatusText>
                              </PublishedStatusButton>
                            ) : (
                              <UnpublishedStatusButton>
                                <UnpublishedStatusText>
                                  미게시
                                </UnpublishedStatusText>
                              </UnpublishedStatusButton>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5">
                          <Body2
                            color="gray500"
                            align="center"
                            style={{ padding: "40px 20px" }}
                          >
                            활성화 코드가 없습니다.
                          </Body2>
                        </td>
                      </tr>
                    )}
                  </PopupTableBody>
                </PopupTable>
              </StyledTableContainer>
            </CustomPopupBody>

            {/* 푸터 */}
            {/* <CustomPopupFooter>
              <CustomButton Secondary onClick={handleCancel}>
                <Body1 color="gray700">취소</Body1>
              </CustomButton>
              <CustomButton
                Primary
                disabled={!isFormValid()}
                onClick={isFormValid() ? handleSave : undefined}
              >
                <Body1 color="white">저장</Body1>
              </CustomButton>
            </CustomPopupFooter> */}
          </CustomPopupContainer>
        </CustomPopupOverlay>
      )}

      {showTemplatePreview && (
        <OrganismTemplatePreview
          template={templateToPreview}
          onClose={handleCloseTemplatePreview}
          title={
            contentList.find((c) => c._id === templateToPreview)?.dataAnalysis
              ?.title
          }
          htmlContent={previewHtml}
          displayWidth={previewWidth}
          displayHeight={previewHeight}
        />
      )}

      {showActivatePopup && (
        <PopupWrap
          Check
          title="활성화 되었습니다."
          buttonType="Outline"
          confirmText="확인"
          isModal={false}
          onConfirm={handleSave}
        />
      )}

      {showDeactivatePopup && (
        <PopupWrap
          Warning
          title="비활성화 시 모든 정보가 삭제됩니다."
          message="비활성화 하시겠습니까?"
          buttonType="Outline"
          closeText="취소"
          confirmText="비활성화"
          isModal={false}
          onCancel={() => {
            setShowDeactivatePopup(false);
            setPendingToggle(null);
          }}
          onConfirm={handleSave}
        />
      )}

      {showConfirmPopup && contentToDelete && (
        <PopupWrap
          Warning
          title="정말 삭제 하시겠습니까?"
          message={<>해당 콘텐츠가 삭제됩니다.</>}
          buttonType="Outline"
          closeText="취소"
          confirmText="삭제"
          isModal={false}
          onCancel={() => {
            setShowConfirmPopup(false);
            setContentToDelete(null);
          }}
          onConfirm={() => {
            setShowConfirmPopup(false);
            handleDeleteContent(contentToDelete);
            setContentToDelete(null);
          }}
        />
      )}
    </>
  );
};

export default OrganismDashboardContentList;

const ContentListWrap = styled.div`
  width: 100%;
`;

const Table = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  background: ${palette.white};
  // border-radius: 10px;
  overflow: hidden;
  // border: 1px solid ${palette.outlineGray};
`;

const TableHeader = styled.thead`
  background: #eef2f7;

  th {
    padding: 16px 20px;
    border-bottom: 1px solid ${palette.outlineGray};

    // &:not(:last-child) {
    //   border-right: 1px solid #D6E0EB;
    // }
  }
`;

const TableBody = styled.tbody`
  tr {
    &:hover {
      background: #f9fafb;
    }

    border-bottom: 1px solid ${palette.outlineGray};

    // &:not(:last-child) {
    //   border-bottom: 1px solid ${palette.outlineGray};
    // }
  }

  td {
    padding: 16px 20px;

    // &:not(:last-child) {
    //   border-right: 1px solid #E5E7EB;
    // }
  }
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 12px;
  border-radius: 20px;
  background: ${(props) =>
    props.status === "활성" ? palette.primary : "#F3F4F6"};
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background: #f3f4f6;
  }
`;

const ToggleCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

// 새로운 팝업 버튼 스타일
const PopupButton = styled.button`
  background: ${palette.brightBlue};
  color: ${palette.primary};
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: ${palette.brightBlue}DD;
  }
`;

// 팝업 내부 스타일들
const PopupContentWrapper = styled.div`
  padding: 20px 0;
  max-height: 400px;
  overflow-y: auto;
  width: 100%;
  min-width: 750px;
`;

const PopupTable = styled.table`
  width: 100%;
  min-width: 750px;
  border-collapse: collapse;
  background: ${palette.white};
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid ${palette.outlineGray};
`;

const PopupTableHeader = styled.thead`
  background: #eef2f7;
  position: sticky;
  top: 0;
  z-index: 1;

  th {
    padding: 16px 20px;
    border-bottom: 1px solid ${palette.outlineGray};
  }
`;

const PopupTableBody = styled.tbody`
  tr {
    &:hover {
      background: #f9fafb;
    }

    &:not(:last-child) {
      border-bottom: 1px solid ${palette.outlineGray};
    }
  }

  td {
    padding: 16px 20px;
    vertical-align: middle;
  }
`;

const ContentInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ContentTitle = styled.div`
  margin-bottom: 8px;
`;

const ContentDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PopupToggleCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

// 토글 스위치 스타일들
const ToggleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 120px; /* 전체 컨테이너 고정 너비 */
`;

const ToggleSwitchArea = styled.div`
  width: 44px; /* 토글 스위치 영역 고정 */
  display: flex;
  justify-content: center;
  cursor: pointer;
`;

const ToggleSlider = styled.div`
  position: relative;
  width: 44px;
  height: 24px;
  background-color: ${(props) => (props.isActive ? "#226FFF" : "#D1D5DB")};
  border-radius: 12px;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: ${(props) => (props.isActive ? "#1E5CE6" : "#B5B9BD")};
  }
`;

const ToggleKnob = styled.div`
  position: absolute;
  top: 2px;
  left: ${(props) => (props.isActive ? "22px" : "2px")};
  width: 20px;
  height: 20px;
  background-color: white;
  border-radius: 50%;
  transition: left 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const ToggleLabelArea = styled.div`
  flex: 1; /* 남은 공간 차지 */
  margin-left: 8px;
  display: flex;
  align-items: center;
`;

const ToggleLabel = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${(props) => (props.isActive ? "#226FFF" : "#6B7280")};
  white-space: nowrap;
`;

const ViewIdSection = styled.div`
  // margin-bottom: 16px;
`;

// 커스텀 팝업 스타일들
const CustomPopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const CustomPopupContainer = styled.div`
  width: 880px;
  max-width: 90vw;
  max-height: 90vh;
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const CustomPopupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 40px 40px 24px 40px;
  background: white;
`;

const CustomPopupTitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const PopupTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
`;

const CustomPopupTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${palette.gray800};
  margin: 0;
`;

const CustomPopupInfoSection = styled.div`
  margin: 8px 0;
`;

const CustomPopupInfoItem = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const CustomPopupSubtitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${palette.gray600};
  margin: 0;
  display: flex;
  align-items: center;
`;

const CustomCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  margin-top: 0;

  &:hover {
    background: ${palette.gray100};
  }
`;

const CustomPopupBody = styled.div`
  padding: 10px;
  overflow-y: auto;
  flex: 1;
  max-height: 500px;
  border-top: none;
`;

const CustomPopupFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px 32px;
  border-top: 1px solid ${palette.outlineGray};
  background: white;
`;

const CustomButton = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid;
  transition: all 0.2s ease;

  ${(props) =>
    props.Primary &&
    `
    background: ${palette.primary};
    border-color: ${palette.primary};
    color: white;
    
    &:hover:not(:disabled) {
      background: ${palette.primary}DD;
    }
    
    &:disabled {
      background: ${palette.gray300};
      border-color: ${palette.gray300};
      cursor: not-allowed;
    }
  `}

  ${(props) =>
    props.Secondary &&
    `
    background: white;
    border-color: ${palette.outlineGray};
    color: ${palette.gray700};
    
    &:hover {
      background: ${palette.gray50};
    }
  `}
`;

// 미리보기 버튼 스타일
const PreviewButton = styled.button`
  background: ${palette.gray100};
  color: ${palette.gray700};
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  &:hover {
    background: ${palette.gray200};
  }
`;

// 삭제 버튼 스타일
const DeleteButton = styled.button`
  color: #666666;
  border: none;
  padding: 4px 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  margin-left: 30px;

  &:hover {
    svg {
      path {
        fill: rgb(73, 73, 73);
      }
    }
  }
`;

// 새로운 스타일 컴포넌트들 추가
const ContentNameCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
`;

const TemplateCategory = styled.div`
  color: ${palette.primary};
  font-weight: 500;
  background-color: ${palette.primaryLightest};
  padding: 3px 8px;
  border-radius: 2px;
`;

const ContentPurpose = styled.div`
  color: #226fff;
  font-weight: 500;
  background-color: #f0f4ff;
  padding: 3px 8px;
  border-radius: 2px;
`;

const TagContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ActivationScore = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ActionButtonsCell = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
`;

const CopyButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  margin-left: 8px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${palette.gray100};
  }
`;

// 게시 상태 스타일 컴포넌트들
const PublishedStatusButton = styled.div`
  padding: 8px 12px;
  background: #f0f4ff;
  border-radius: 4px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
`;

const UnpublishedStatusButton = styled.div`
  padding: 8px 12px;
  background: #f7f8fa;
  border-radius: 4px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
`;

const PublishedStatusText = styled.span`
  color: #226fff;
  font-size: 14px;
  font-family: Pretendard;
  font-weight: 500;
  line-height: 16.8px;
  word-wrap: break-word;
`;

const UnpublishedStatusText = styled.span`
  color: #666666;
  font-size: 14px;
  font-family: Pretendard;
  font-weight: 500;
  line-height: 16.8px;
  word-wrap: break-word;
`;

// 팝업 상단 정보 섹션을 위한 새로운 스타일 컴포넌트들
const StyledInfoSpan = styled.span`
  color: var(--color-text-subtle, #464c53);
  font-size: 17px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 25.5px;
  word-wrap: break-word;
`;

const StyledDivider = styled.div`
  width: 1px;
  height: 16px;
  background: var(--color-divider-gray-light, #cdd1d5);
`;

const StyledIdSpan01 = styled.span`
  color: var(--color-text-subtle, #464c53);
  font-size: 17px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 25.5px;
  word-wrap: break-word;
`;

const StyledIdSpan02 = styled.span`
  color: var(--color-text-primary, #0b50d0);
  font-size: 17px;
  font-family: Pretendard;
  font-weight: 700;
  line-height: 25.5px;
  word-wrap: break-word;
`;

const StyledMeta = styled.div`
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  display: inline-flex;
  flex-wrap: wrap;
  align-content: center;
`;

const StyledLabel = styled.div`
  align-self: stretch;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  display: flex;
`;

const StyledFrame = styled.div`
  align-self: stretch;
  justify-content: space-between;
  align-items: center;
  display: inline-flex;
`;

const StyledInfoContainer = styled.div`
  width: 800px;
  margin: 8px auto 0px auto;
  padding: 20px;
  border-radius: 10px;
  outline: 1px rgba(179, 183, 186, 0.5) solid;
  outline-offset: -1px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 12px;
  display: inline-flex;
  position: relative;
`;

const StyledTableContainer = styled.div`
  width: 800px;
  margin: 0 auto;
`;
